import { useLayoutEffect, useRef, useState, type DependencyList, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { findScrollParent, offsetWithin } from '@/lib/scrollParent';

// A collapsed row's height: two lines of text in a 56px row. Measuring corrects it as each row mounts.
const ROW_ESTIMATE_PX = 57;
// Rows kept mounted just past the visible window, so a fast scroll never flashes blank rows.
const ROW_OVERSCAN = 10;
// Rows drawn on the first render, before the page scroller is found — about a screenful, never the whole book.
const FIRST_PAINT_ROWS = 20;

/**
 * A fundraising table's rows, windowed against the page's own scroller (the admin shell's `<main>`), so a
 * long list mounts only the rows in view. Put `listRef` on an element holding the table and `bodyRef` on its
 * `<tbody>`, pass each mounted row `measure`, and draw the body with `rows`.
 *
 * `remeasureOn` lists whatever above the rows can move where they start (a toolbar, a note, an error). Where
 * they start is re-read only when one of those changes — never after the virtualizer's own re-renders: the
 * table is laid out from the rows in view, so re-reading after every render fed which rows mounted back into
 * where they start, and the two chased each other until React gave up.
 */
export function useWindowedRows(count: number, remeasureOn: DependencyList) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);
  // Undefined until the layout effect below finds the scroller; null if there is none.
  const [scrollEl, setScrollEl] = useState<HTMLElement | null | undefined>(undefined);
  const [scrollMargin, setScrollMargin] = useState(0);
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollEl ?? null,
    estimateSize: () => ROW_ESTIMATE_PX,
    overscan: ROW_OVERSCAN,
    scrollMargin,
  });
  // Found again once the list mounts, for a table drawn only after its data arrives.
  const mounted = count > 0;
  useLayoutEffect(() => {
    setScrollEl(findScrollParent(listRef.current));
  }, [mounted]);
  useLayoutEffect(() => {
    if (!scrollEl || !bodyRef.current) return;
    setScrollMargin(Math.round(offsetWithin(bodyRef.current, scrollEl)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller names what can move the rows
  }, [scrollEl, mounted, ...remeasureOn]);
  // Before the scroller is found there is nothing to window against yet, but it is moments away: draw a
  // screenful, not every row — mounting a whole 5,000-person book first blocked the page for seconds.
  // jsdom and a not-yet-laid-out page have no height to window against, so every row renders.
  const mode: 'first-paint' | 'all' | 'windowed' =
    scrollEl === undefined ? 'first-paint' : scrollEl == null || scrollEl.clientHeight === 0 ? 'all' : 'windowed';

  /** The body's rows: every one, or the window with spacers standing in for the rest of the scroll. */
  const rows = (columns: number, renderRow: (index: number) => ReactNode): ReactNode => {
    if (mode !== 'windowed') {
      const drawn = mode === 'first-paint' ? Math.min(count, FIRST_PAINT_ROWS) : count;
      return Array.from({ length: drawn }, (_, index) => renderRow(index));
    }
    const inView = virtualizer.getVirtualItems();
    const topPad = inView.length ? inView[0].start - scrollMargin : 0;
    const bottomPad = inView.length ? virtualizer.getTotalSize() - (inView[inView.length - 1].end - scrollMargin) : 0;
    return (
      <>
        {topPad > 0 && (
          <tr aria-hidden>
            <td colSpan={columns} style={{ height: topPad }} />
          </tr>
        )}
        {inView.map((v) => renderRow(v.index))}
        {bottomPad > 0 && (
          <tr aria-hidden>
            <td colSpan={columns} style={{ height: bottomPad }} />
          </tr>
        )}
      </>
    );
  };

  return {
    listRef,
    bodyRef,
    /** The virtualizer's measuring ref for each mounted row, or undefined when the rows aren't windowed. */
    measure: mode === 'windowed' ? virtualizer.measureElement : undefined,
    rows,
  };
}
